import {
    PlusIcon,
    TrashIcon
} from "@primer/octicons-react";
import { Box, Checkbox } from "@primer/react";
import { useCallback, useEffect, useState } from "react";
import { hinhThucMienGiamApi } from "../../../api/hinhThucMienGiamApi";
import { khoanNopApi } from "../../../api/khoanNopApi";
import DataGrid from "../../../components/data-grid";
import { IDataGridColumn } from "../../../components/data-grid/type/IDataGridProps";
import Button from "../../../components/ui/button";
import MyIconButton from "../../../components/ui/button/IconButton";
import { showNotify } from "../../../helpers/toast";
import { sf_hinhthucmiengiam } from "../../../models/response/category/sf_hinhthucmiengiam";
import { sf_hinhthucmiengiam_chitiet_viewmodel } from "../../../models/response/category/sf_hinhthucmiengiam_chitiet";
import { MienGiamEditItemsModal } from "./edit_modal";

export type MienGiamItemsSubmitPayload = {
    id: number;
    sf_hinhthucmiengiam_id: number;
    sf_hinhthucmiengiam_chitiet_id: number;
    phan_tram_mien_giam: number;
    so_tien_mien_giam: number;
    is_huonguudai_duynhat?: boolean;
};

export type MienGiamItemsSubmitHandler = () => MienGiamItemsSubmitPayload[];

type PendingDetailRow = sf_hinhthucmiengiam_chitiet_viewmodel & {
    is_huonguudai_duynhat?: boolean;
    sf_hinhthucmiengiam_chitiet_id?: number;
    nhom_khoan_nop?: string;
};

type MienGiamItemsModalProps = {
    animationOf: string,
    sf_hinhthucmiengiam: sf_hinhthucmiengiam,
    onClose: () => void,
    onEditRequest: () => void,
    onRegisterSubmit?: (submitFn: MienGiamItemsSubmitHandler | null) => void,
    onSavingChange?: (isSaving: boolean) => void,
}

export const MienGiamItemsModal = (props: MienGiamItemsModalProps) => {
    const [sf_hinhthucmiengiam_chitiets, set_sf_hinhthucmiengiam_chitiets] = useState<PendingDetailRow[]>([]);
    const [isShowEditPopUp, SetIsShowEditPopUp] = useState<boolean>(false);
    const [pendingChanges, setPendingChanges] = useState<Record<number, PendingDetailRow>>({});
    const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);

    useEffect(() => {
        getHinhThucMienGiamChiTiets(props.sf_hinhthucmiengiam.id);
    }, [props.sf_hinhthucmiengiam.id]);

    useEffect(() => {
        props.onSavingChange?.(false);
    }, [props.onSavingChange]);

    const getHinhThucMienGiamChiTiets = async (sf_hinhthucmiengiam_id: number) => {
        let isLoading: boolean = true;
        const interval_obj = setInterval(() => {
            if (!isLoading) {
                clearInterval(interval_obj);
            }
        }, 800);

        const [res, khoanNopRes] = await Promise.all([
            hinhThucMienGiamApi.select_chitiets(sf_hinhthucmiengiam_id),
            khoanNopApi.select_by_miengiam(sf_hinhthucmiengiam_id),
        ]);

        const nhomKhoanNopByKhoanNopId: Record<number, string> = {};
        if (khoanNopRes.is_success && Array.isArray(khoanNopRes.data)) {
            khoanNopRes.data.forEach((item: any) => {
                nhomKhoanNopByKhoanNopId[item.id] = item.nhom_khoan_nop || "Khác";
            });
        }

        if (res.is_success) {
            set_sf_hinhthucmiengiam_chitiets(
                res.data
                    .map((item: PendingDetailRow) => ({
                        ...item,
                        sf_hinhthucmiengiam_chitiet_id: item.sf_hinhthucmiengiam_chitiet_id || item.id,
                        nhom_khoan_nop: nhomKhoanNopByKhoanNopId[item.sf_khoannop_id] || "Khác",
                    }))
                    .sort((a: any, b: any) =>
                        a.thutu_sapxep_nhomkhoannop - b.thutu_sapxep_nhomkhoannop || a.thutu_sapxep - b.thutu_sapxep
                    )
            );
            setPendingChanges({});
            setSelectedRowKeys([]);
        } else {
            set_sf_hinhthucmiengiam_chitiets([]);
            setPendingChanges({});
            setSelectedRowKeys([]);
        }
        isLoading = false;
    };

    const upsertPendingChange = (updatedItem: PendingDetailRow) => {
        setPendingChanges((prev) => ({
            ...prev,
            [updatedItem.id]: {
                ...(prev[updatedItem.id] || updatedItem),
                ...updatedItem,
            },
        }));
    };

    const handleDeleteMienGiamChiTiet = async (ids: number[]) => {
        if (ids.length === 0) {
            return;
        }

        const responses = await Promise.all(ids.map((id) => hinhThucMienGiamApi.deleteChiTiet(id)));
        const hasError = responses.some((res) => !res.is_success);

        if (hasError) {
            const errorMessage = responses.find((res) => !res.is_success)?.message || "Xóa khoản miễn giảm thất bại";
            showNotify({
                message: errorMessage,
                type: "error",
            });
            return;
        }

        showNotify({
            message: ids.length > 1 ? "Xóa các khoản miễn giảm thành công" : "Xóa khoản miễn giảm thành công",
            type: "success",
        });
        getHinhThucMienGiamChiTiets(props.sf_hinhthucmiengiam.id);
    };

    const handleCheckBoxChange = (e: React.ChangeEvent<HTMLInputElement>, cellData: PendingDetailRow) => {
        const checked = e.target.checked;
        let updatedItem: PendingDetailRow | null = null;

        set_sf_hinhthucmiengiam_chitiets((prev) =>
            prev.map((item) => {
                if (item.id !== cellData.id) {
                    return item;
                }

                updatedItem = {
                    ...item,
                    is_huonguudai_duynhat: checked,
                };
                return updatedItem;
            })
        );

        if (updatedItem) {
            upsertPendingChange(updatedItem);
        }
    };

    const handleInlineValueChange = (
        id: number,
        field: "phan_tram_mien_giam" | "so_tien_mien_giam",
        value: string
    ) => {
        let updatedItem: PendingDetailRow | null = null;

        set_sf_hinhthucmiengiam_chitiets((prev) =>
            prev.map((item) => {
                if (item.id !== id) {
                    return item;
                }

                updatedItem = {
                    ...item,
                    [field]: value === "" ? 0 : Number(value),
                };
                return updatedItem;
            })
        );

        if (updatedItem) {
            upsertPendingChange(updatedItem);
        }
    };

    const handleSubmitChanges = useCallback((): MienGiamItemsSubmitPayload[] => {
        return Object.values(pendingChanges).map((item) => ({
            id: item.sf_khoannop_id,
            sf_hinhthucmiengiam_id: props.sf_hinhthucmiengiam.id,
            sf_hinhthucmiengiam_chitiet_id:
                item.sf_hinhthucmiengiam_chitiet_id || item.id,
            phan_tram_mien_giam: Number(item.phan_tram_mien_giam || 0),
            so_tien_mien_giam: Number(item.so_tien_mien_giam || 0),
            is_huonguudai_duynhat: item.is_huonguudai_duynhat,
        }));
    }, [pendingChanges, props.sf_hinhthucmiengiam.id]);

    useEffect(() => {
        props.onRegisterSubmit?.(handleSubmitChanges);

        return () => {
            props.onRegisterSubmit?.(null);
        };
    }, [handleSubmitChanges, props.onRegisterSubmit]);

    const renderEditableNumberCell = (
        cellData: PendingDetailRow,
        field: "phan_tram_mien_giam" | "so_tien_mien_giam"
    ) => {
        return (
            <Box sx={{ px: 1, py: 1 }}>
                <input
                    type="number"
                    value={cellData[field] ?? ""}
                    onChange={(e) => {
                        handleInlineValueChange(cellData.id, field, e.target.value);
                    }}
                    style={{
                        width: "100%",
                        minWidth: "60px",
                        height: "30px",
                        padding: "0 8px",
                        border: "1px solid #d0d7de",
                        borderRadius: "6px",
                        textAlign: "right",
                        outline: "none",
                        backgroundColor: "#ffffff",
                    }}
                />
            </Box>
        );
    };

    const columns: IDataGridColumn[] = [
        {
            caption: "Mã khoản nộp",
            id: "ma_khoan_nop",
            dataField: "ma_khoan_nop",
            width: 100,
        },
        {
            caption: "Khoản nộp",
            id: "khoan_nop",
            dataField: "ten_khoan_nop",
            width: 200,
        },
        {
            caption: "Phần trăm (%)",
            id: "phan_tram_mien_giam",
            dataField: "phan_tram_mien_giam",
            width: 120,
            cellRender: (data: PendingDetailRow) => renderEditableNumberCell(data, "phan_tram_mien_giam"),
        },
        {
            caption: "Số tiền",
            id: "so_tien_mien_giam",
            dataField: "so_tien_mien_giam",
            width: 100,
            align: "right",
            cellRender: (data: PendingDetailRow) => renderEditableNumberCell(data, "so_tien_mien_giam"),
        },
        {
            caption: "Ưu đãi duy nhất",
            id: "is_huonguudai_duynhat",
            dataField: "is_huonguudai_duynhat",
            width: 140,
            cellRender: (data: PendingDetailRow) => (
                <Box sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    py: 1
                }}>
                    <Checkbox
                        checked={data.is_huonguudai_duynhat === true}
                        aria-label="Hưởng duy nhất ưu đãi này"
                        onChange={(e) => handleCheckBoxChange(e, data)}
                        sx={{
                            border: "2px solid #d1d9e0",
                            "&:checked": {
                                backgroundColor: "#0969da",
                                borderColor: "#0969da"
                            }
                        }}
                    />
                </Box>
            ),
        },
    ];

    return (
        <Box>
            <DataGrid
                dataSource={sf_hinhthucmiengiam_chitiets}
                columns={columns}
                keyExpr="id"
                height={window.innerHeight - 240}
                isShowRowIdx={false}
                grouping={{
                    enable: true,
                    groupField: "nhom_khoan_nop",
                    defaultExpanded: true,
                }}
                selection={{
                    mode: "multiple",
                    selectedRowKeys,
                    onSelectionChanged: (keys: number[]) => {
                        setSelectedRowKeys(keys);
                    },
                }}
                actionComponent={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        {selectedRowKeys.length > 0 && (
                            <Button
                                variant="danger"
                                size="small"
                                leadingVisual={TrashIcon}
                                onClick={() => {
                                    handleDeleteMienGiamChiTiet(selectedRowKeys);
                                }}
                            >
                                Xóa khoản nộp ({selectedRowKeys.length})
                            </Button>
                        )}
                        <Button
                            variant="primary"
                            size="small"
                            leadingVisual={PlusIcon}
                            onClick={() => SetIsShowEditPopUp(true)}
                        >
                            Thêm mới
                        </Button>
                    </Box>
                }
            />
            {isShowEditPopUp && <MienGiamEditItemsModal
                animationOf="#btnEditItems"
                onChanged={() => {
                    SetIsShowEditPopUp(false);
                    getHinhThucMienGiamChiTiets(props.sf_hinhthucmiengiam.id);
                }}
                onClose={() => { SetIsShowEditPopUp(false); }}
                sf_hinhthucmiengiam={props.sf_hinhthucmiengiam}
            />}
        </Box>
    );
};
